import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Package,
  Plus,
  Trash2,
  Loader2,
  Sparkles,
  Check,
  ChevronDown,
  ChevronUp,
  Shirt,
  Droplets,
  Zap,
  FileText,
  Heart,
  Watch,
  Tent,
  Box,
} from "lucide-react";

const CATEGORIES = [
  { id: "clothing", name: "Clothing", icon: Shirt, color: "text-sky-400" },
  { id: "toiletries", name: "Toiletries", icon: Droplets, color: "text-pink-400" },
  { id: "electronics", name: "Electronics", icon: Zap, color: "text-amber-400" },
  { id: "documents", name: "Documents", icon: FileText, color: "text-emerald-400" },
  { id: "health", name: "Health", icon: Heart, color: "text-red-400" },
  { id: "accessories", name: "Accessories", icon: Watch, color: "text-violet-400" },
  { id: "gear", name: "Gear", icon: Tent, color: "text-orange-400" },
  { id: "general", name: "General", icon: Box, color: "text-zinc-400" },
];

const getCatInfo = (catId) => CATEGORIES.find((c) => c.id === catId) || CATEGORIES[7];

export const PackingList = ({ tripId, destination, startDate, endDate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemCat, setNewItemCat] = useState("general");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (expanded && items.length === 0) fetchItems();
  }, [expanded]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/trips/${tripId}/packing`, { withCredentials: true });
      setItems(res.data);
    } catch {
      toast.error("Failed to load packing list");
    } finally {
      setLoading(false);
    }
  };

  const addItem = async () => {
    if (!newItemName.trim()) return;
    try {
      const res = await axios.post(
        `${API}/trips/${tripId}/packing`,
        { name: newItemName.trim(), category: newItemCat },
        { withCredentials: true }
      );
      setItems((prev) => [...prev, res.data]);
      setNewItemName("");
      toast.success("Item added");
    } catch {
      toast.error("Failed to add item");
    }
  };

  const togglePacked = async (item) => {
    try {
      const res = await axios.put(
        `${API}/trips/${tripId}/packing/${item.item_id}`,
        { packed: !item.packed },
        { withCredentials: true }
      );
      setItems((prev) => prev.map((i) => (i.item_id === item.item_id ? res.data : i)));
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/trips/${tripId}/packing/${itemId}`, { withCredentials: true });
      setItems((prev) => prev.filter((i) => i.item_id !== itemId));
    } catch {
      toast.error("Failed to delete");
    }
  };

  const getAISuggestions = async () => {
    setSuggesting(true);
    setShowSuggestions(true);
    try {
      const res = await axios.post(
        `${API}/trips/${tripId}/packing/suggest`,
        { destination, start_date: startDate, end_date: endDate },
        { withCredentials: true }
      );
      const existing = new Set(items.map((i) => i.name.toLowerCase()));
      const filtered = (res.data.suggestions || []).filter(
        (s) => !existing.has(s.name.toLowerCase())
      );
      setSuggestions(filtered);
      if (filtered.length === 0) toast.info("No new suggestions — you've got everything covered!");
    } catch {
      toast.error("AI suggestions failed");
    } finally {
      setSuggesting(false);
    }
  };

  const addSuggestions = async (selected) => {
    const toAdd = selected.map((s) => ({ name: s.name, category: s.category || "general" }));
    try {
      const res = await axios.post(`${API}/trips/${tripId}/packing/bulk`, toAdd, { withCredentials: true });
      setItems((prev) => [...prev, ...res.data]);
      setSuggestions([]);
      setShowSuggestions(false);
      toast.success(`${toAdd.length} items added`);
    } catch {
      toast.error("Failed to add suggestions");
    }
  };

  const grouped = useMemo(() => {
    const groups = {};
    items.forEach((item) => {
      const cat = item.category || "general";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [items]);

  const packedCount = items.filter((i) => i.packed).length;
  const totalCount = items.length;

  return (
    <div className="mt-3" data-testid={`packing-list-${tripId}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors"
        data-testid={`toggle-packing-${tripId}`}
      >
        <Package className="w-4 h-4" />
        <span>Packing List</span>
        {totalCount > 0 && (
          <span className="text-xs text-zinc-500">
            ({packedCount}/{totalCount})
          </span>
        )}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="mt-2 p-3 bg-white/5 rounded-lg space-y-3" data-testid={`packing-panel-${tripId}`}>
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
            </div>
          ) : (
            <>
              {/* Add new item */}
              <div className="flex gap-2">
                <Input
                  placeholder="Add item..."
                  className="glass-input flex-1 h-9 text-sm"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addItem()}
                  data-testid={`packing-new-input-${tripId}`}
                />
                <Select value={newItemCat} onValueChange={setNewItemCat}>
                  <SelectTrigger className="glass-input w-28 h-9 text-sm" data-testid={`packing-cat-select-${tripId}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="glass-card border-white/10">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="text-sm">{c.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="btn-primary h-9" onClick={addItem} data-testid={`packing-add-btn-${tripId}`}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* AI Suggestions button */}
              <Button
                size="sm"
                variant="outline"
                className="w-full btn-outline text-sm h-8"
                onClick={getAISuggestions}
                disabled={suggesting}
                data-testid={`packing-ai-suggest-${tripId}`}
              >
                {suggesting ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1 text-amber-400" />
                )}
                {suggesting ? "Generating..." : "AI Suggest Items"}
              </Button>

              {/* AI Suggestions list */}
              {showSuggestions && suggestions.length > 0 && (
                <SuggestionsList suggestions={suggestions} onAdd={addSuggestions} onDismiss={() => setShowSuggestions(false)} />
              )}

              {/* Progress bar */}
              {totalCount > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-zinc-400">
                    <span>{packedCount} of {totalCount} packed</span>
                    <span>{Math.round((packedCount / totalCount) * 100)}%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full transition-all duration-300"
                      style={{ width: `${(packedCount / totalCount) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Items grouped by category */}
              {Object.entries(grouped).map(([catId, catItems]) => {
                const cat = getCatInfo(catId);
                const CatIcon = cat.icon;
                return (
                  <div key={catId}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <CatIcon className={`w-3.5 h-3.5 ${cat.color}`} />
                      <span className={`text-xs font-medium ${cat.color}`}>{cat.name}</span>
                    </div>
                    <div className="space-y-1">
                      {catItems.map((item) => (
                        <div
                          key={item.item_id}
                          className="flex items-center gap-2 group"
                          data-testid={`packing-item-${item.item_id}`}
                        >
                          <button
                            onClick={() => togglePacked(item)}
                            className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                              item.packed
                                ? "bg-emerald-500 border-emerald-500"
                                : "border-zinc-600 hover:border-zinc-400"
                            }`}
                            data-testid={`pack-toggle-${item.item_id}`}
                          >
                            {item.packed && <Check className="w-3 h-3 text-white" />}
                          </button>
                          <span
                            className={`flex-1 text-sm ${
                              item.packed ? "line-through text-zinc-600" : "text-zinc-300"
                            }`}
                          >
                            {item.name}
                          </span>
                          <button
                            onClick={() => deleteItem(item.item_id)}
                            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/10 rounded transition-all"
                            data-testid={`packing-delete-${item.item_id}`}
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {totalCount === 0 && !loading && (
                <p className="text-xs text-zinc-500 text-center py-2">
                  No items yet. Add manually or let AI suggest!
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// AI Suggestions sub-component
const SuggestionsList = ({ suggestions, onAdd, onDismiss }) => {
  const [selected, setSelected] = useState(new Set(suggestions.map((_, i) => i)));

  const toggle = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  return (
    <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="ai-suggestions-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-amber-400">AI Suggestions</span>
        <div className="flex gap-1">
          <Button
            size="sm"
            className="h-6 text-xs btn-primary px-2"
            onClick={() => onAdd(suggestions.filter((_, i) => selected.has(i)))}
            data-testid="add-selected-suggestions"
          >
            Add {selected.size} items
          </Button>
          <Button size="sm" variant="ghost" className="h-6 text-xs text-zinc-400 px-2" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
        {suggestions.map((s, i) => {
          const cat = getCatInfo(s.category);
          return (
            <button
              key={i}
              onClick={() => toggle(i)}
              className={`flex items-center gap-1.5 text-left text-xs p-1.5 rounded transition-colors ${
                selected.has(i) ? "bg-amber-500/20 text-amber-300" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <div
                className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                  selected.has(i) ? "bg-amber-500 border-amber-500" : "border-zinc-600"
                }`}
              >
                {selected.has(i) && <Check className="w-2.5 h-2.5 text-white" />}
              </div>
              <span className="truncate">{s.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
